import Input from "components/EvtKit/input/Input"
import ApiAccounts from "api/ApiAccounts"
import { statusLabels } from "constants/accounts"
import { columns } from "constants/accounts-table"
import { AppState, useAppSelector } from "reducers/store"
import { memo, useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import styled from "styled-components"
import Heading from "components/Typography/Heading"
import { useQuery } from "hooks/useQuery"
import { useDebouncedCallback } from "use-debounce"
import { useQueryState } from "context/queryState"
import { ACCOUNTS_FETCH } from "reducers/accounts/actions-types"
import Caption from "components/Typography/Caption"
import { BsArrowClockwise } from "react-icons/bs"
import { BeatTable } from "components/Beat/BeatTable/layout"
import SelectV2, { Option } from "components/EvtKit/select/SelectV2"
import ApiProducts from "api/ApiProducts"
import { productsMapperSelect } from "utils/mapper/event-types"
import { SET_PRODUCT_LIST } from "reducers/products/actions-types"
import { useDeepMemo } from "utils/useDeepMemo"

interface ContainerProps {
  align?: string
  direction?: string
  justify?: string
}

const Container = styled.div<ContainerProps>`
  width: 100%;
  height: 100%;
  background: ${(props) => props.theme.palette.gray_ice};
  align-items: center;
`

export const Title = styled.span`
  display: flex;
  align-items: center;
  padding: 48px 0px 48px 0px;
`
const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 24px 24px 0px;
`

const ContainerGeneralFilters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ContainerTitleClearFilters = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const ClearFiltersContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  flex-direction: row;
  cursor: pointer;
  gap: 4px;
  padding: 8px 20px 8px 20px;
  border-radius: 40px;
  border: 1px solid;

  color: ${(props) => props.theme.palette.primary_one};

  &.disabled {
    color: ${(props) => props.theme.palette.disabled};
    cursor: not-allowed;
  }
`

const ClearFiltersIconContainer = styled.div`
  font-size: 20px;
  transform: rotate(45deg);
  color: ${(props) => props.theme.palette.primary_one};

  &.disabled {
    color: ${(props) => props.theme.palette.disabled};
  }
`

const ContainerFilters = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  background-color: ${(props) => props.theme.palette.white};
  padding: 24px 24px 16px 24px;
  border-radius: 10px;
  box-shadow: 0px 10px 56px rgba(0, 0, 0, 0.05);
  align-items: center;
  margin-bottom: 24px;
`

const StyledInput = styled(Input)`
  width: 100%;
  max-width: 400px;

  label {
    width: fit-content;
  }
`

const ContainerSelector = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 400px;
`

const TableSection = styled.div`
  table {
    td:last-child {
      width: 228px;
    }
  }
`

const initialDebouncedFilters: App.AccountFilters = {
  name: undefined
}

const initialFilters: App.AccountFilters = {
  ...initialDebouncedFilters,
  status: undefined,
  product: undefined
}

const AccountsTable = () => {
  const navigate = useNavigate()
  const queryParams = useQuery()

  const { listAccounts } = useAppSelector((x: AppState) => x.accounts)

  const [filters, setFilters] = useState<App.AccountFilters>(initialFilters)
  const [debouncedFilters, setDebouncedFilters] = useState<App.AccountFilters>(initialDebouncedFilters)
  const [skip, setSkip] = useState<string>(queryParams.get("skip") ?? "0")
  const [take, setTake] = useState<string>(queryParams.get("take") ?? "10")

  const memoizedFilters = useDeepMemo(() => filters, [filters])

  const searchFilters = {
    name: filters.name,
    status: filters.status,
    product: filters.product,
    skip: skip,
    take: take
  }

  const debouncedFiltersCallback = useDebouncedCallback(() => {
    setFilters((state) => ({
      ...state,
      ...debouncedFilters
    }))
  }, 1000)

  useEffect(() => {
    debouncedFiltersCallback()
  }, [debouncedFilters, debouncedFiltersCallback])

  useQueryState(
    [ACCOUNTS_FETCH, searchFilters],
    async () =>
      new ApiAccounts().searchAccounts({
        ...filters,
        skip: Number(skip),
        take: Number(take)
      }),
    ACCOUNTS_FETCH
  )

  useQueryState([SET_PRODUCT_LIST], async () => (await new ApiProducts().getAll()).products, SET_PRODUCT_LIST, {
    refetchOnMount: "always"
  })

  const { productList } = useAppSelector((state) => state.product)

  const clearFilters = useCallback(() => {
    setFilters(initialFilters)
    setDebouncedFilters(initialDebouncedFilters)
  }, [])

  const handleNavigate = useCallback(
    (params) => {
      const queries = new URLSearchParams(params.toString().split("?")[1])

      const skip = parseInt(queries.get("skip") ?? "0")
      const take = parseInt(queries.get("take") ?? "10")

      setSkip(skip.toString())
      setTake(take.toString())

      return navigate
    },
    [navigate]
  )

  const enableClearFilter = useMemo(() => Object.values(filters).some((item) => !!item), [filters])

  const handleFiltersChange = useCallback((key: string, value: string) => {
    setFilters((prevState) => ({
      ...prevState,
      [key]: value || undefined
    }))
  }, [])

  const handleProductChange = useCallback((option: Option | null) => {
    handleFiltersChange("product", option?.value || "")
  }, [])
  const handleStatusChange = useCallback((option: Option | null) => {
    handleFiltersChange("status", option?.value || "")
  }, [])

  const handleInputChange = useCallback((e) => {
    setDebouncedFilters((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value || undefined
    }))
  }, [])

  const getDataToProducts = useCallback(async () => {
    const prodOptions = productsMapperSelect(productList.data)

    return {
      hasMore: false,
      results: prodOptions
    }
  }, [productList])

  return (
    <Container align="flex-start">
      <Title>
        <Heading variant="h3">Contas</Heading>
      </Title>
      <Content>
        <ContainerGeneralFilters>
          <ContainerTitleClearFilters>
            <Heading variant="h5" color="gray-dark">
              Filtros
            </Heading>

            {enableClearFilter && (
              <ClearFiltersContainer onClick={clearFilters}>
                <ClearFiltersIconContainer>
                  <BsArrowClockwise />
                </ClearFiltersIconContainer>
                <Caption size="large" color="primary-dark" weight="semi-bold">
                  Limpar Filtros
                </Caption>
              </ClearFiltersContainer>
            )}

            {!enableClearFilter && (
              <ClearFiltersContainer className="disabled">
                <ClearFiltersIconContainer className="disabled">
                  <BsArrowClockwise />
                </ClearFiltersIconContainer>
                <Caption size="large" color="gray-light" weight="semi-bold">
                  Limpar Filtros
                </Caption>
              </ClearFiltersContainer>
            )}
          </ContainerTitleClearFilters>
          <ContainerFilters>
            <StyledInput
              label="Filtrar pelo nome"
              name="name"
              onChange={handleInputChange}
              placeholder="Digite o nome"
              shrink
              value={debouncedFilters.name || ""}
            />

            <ContainerSelector>
              <SelectV2
                id="product"
                label="Filtrar por produto"
                onChange={handleProductChange}
                placeholder="Todos"
                shrink
                getData={getDataToProducts}
                value={filters.product || ""}
                className="fullWidth"
              />
            </ContainerSelector>

            <ContainerSelector>
              <SelectV2
                id="status"
                label="Filtrar por status"
                onChange={handleStatusChange}
                placeholder="Todos"
                shrink
                value={filters.status || ""}
                className="fullWidth"
                options={statusLabels}
              />
            </ContainerSelector>
          </ContainerFilters>
        </ContainerGeneralFilters>

        <TableSection>
          <BeatTable
            columnsjson={columns}
            data={listAccounts.accounts}
            loading={listAccounts.loading}
            totalItems={listAccounts.totalItems}
            initialPage={Number(skip) / Number(take)}
            initialTake={Number(take)}
            navigate={handleNavigate}
            applyParamsToQuery
            filters={memoizedFilters}
          />
        </TableSection>
      </Content>
    </Container>
  )
}

export default memo(AccountsTable)
